import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit3, Landmark, Building2 } from 'lucide-react';
import { useAuth } from '../lib/authStore';
import LenderModal from '../components/lenders/LenderModal';
import LenderLogo from '../components/lenders/LenderLogo';

const API = process.env.REACT_APP_BACKEND_URL;

// Channel → pill styling. Broker is a cool blue, correspondent leans green,
// and "both" splits the difference in neutral so a lender that offers both
// doesn't scream at the user from the grid.
const CHANNEL_META = {
  broker:        { label: 'Broker',        cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  correspondent: { label: 'Correspondent', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  both:          { label: 'Broker · Corr', cls: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
};

function AeAvatar({ name }) {
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
  return (
    <div
      aria-hidden="true"
      className="h-7 w-7 shrink-0 rounded-full bg-neutral-100 border hairline flex items-center justify-center text-[9.5px] font-bold text-neutral-600 tabular-nums"
    >
      {initials || '—'}
    </div>
  );
}

// Format a raw phone string as (XXX) XXX-XXXX. Falls back to the input on
// malformed data so we don't hide a broken number the LO might still need
// to fix.
function fmtPhone(p) {
  const d = (p || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || '';
}

function LenderCard({ lender, isAdmin, onEdit }) {
  const chan = CHANNEL_META[lender.channel] || CHANNEL_META.broker;
  const stop = (e) => e.stopPropagation();
  const hasAe = lender.ae?.name || lender.ae?.phone || lender.ae?.email;
  return (
    <div
      data-testid={`lender-card-${lender.id}`}
      onClick={() => onEdit(lender)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(lender); } }}
      className="group relative rounded-xl border hairline bg-white p-4 flex flex-col gap-3 cursor-pointer transition hover:border-neutral-300 hover:shadow-[0_8px_24px_-16px_rgba(0,0,0,0.18)] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
    >
      {/* Gold hair-thin left accent on hover — subtle brand touch. */}
      <span className="absolute inset-y-3 left-0 w-[2px] rounded-full bg-[hsl(var(--kf-gold))] opacity-0 group-hover:opacity-100 transition" aria-hidden="true" />

      {/* Header row — logo, name + channel/comp badges, edit icon */}
      <div className="flex items-start gap-3">
        <LenderLogo lender={lender} size={40} />
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-bold text-neutral-900 truncate leading-tight" title={lender.name}>{lender.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-bold uppercase tracking-wider">
            <span className={`inline-flex items-center rounded px-1.5 py-0.5 border ${chan.cls}`}>{chan.label}</span>
            {lender.compPct != null && (
              <span className="tabular-nums text-neutral-500">
                <span className="text-neutral-800">{Number(lender.compPct).toFixed(3)}%</span> comp
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid={`lender-card-edit-${lender.id}`}
          onClick={(e) => { e.stopPropagation(); onEdit(lender); }}
          className="shrink-0 h-7 w-7 -mr-1 rounded flex items-center justify-center text-neutral-300 hover:text-neutral-900 hover:bg-neutral-100 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
          title={isAdmin ? 'Edit lender' : 'Edit AE contact'}
          aria-label={isAdmin ? 'Edit lender' : 'Edit AE contact'}
        >
          <Edit3 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Specialties — flow inline, no fixed vertical stretch. Empty state
          renders nothing so cards with no tags feel intentionally compact
          rather than half-empty. */}
      {lender.specialties?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {lender.specialties.slice(0, 8).map((s) => (
            <span key={s} className="text-[10.5px] rounded border hairline bg-neutral-50 text-neutral-700 px-1.5 py-0.5 leading-tight">
              {s}
            </span>
          ))}
          {lender.specialties.length > 8 && (
            <span className="text-[10.5px] rounded border hairline bg-neutral-50 text-neutral-500 px-1.5 py-0.5 leading-tight" title={lender.specialties.slice(8).join(', ')}>
              +{lender.specialties.length - 8}
            </span>
          )}
        </div>
      )}

      {/* AE row — hairline-soft rule + click-to-call / mailto so LOs can
          reach out straight from the grid. `onClick` stopPropagation
          keeps the row-open behaviour from firing when tapping a link. */}
      {hasAe ? (
        <div className="mt-1 pt-3 border-t hairline-soft flex items-center gap-2.5 min-w-0">
          <AeAvatar name={lender.ae?.name} />
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold text-neutral-900 truncate leading-tight">
              {lender.ae?.name || <span className="text-neutral-400 font-normal">No AE</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10.5px] text-neutral-500 tabular-nums leading-tight">
              {lender.ae?.phone && (
                <a
                  href={`tel:${(lender.ae.phone || '').replace(/\D/g, '')}`}
                  onClick={stop}
                  className="hover:text-neutral-900 transition"
                >{fmtPhone(lender.ae.phone)}</a>
              )}
              {lender.ae?.phone && lender.ae?.email && <span className="text-neutral-300">·</span>}
              {lender.ae?.email && (
                <a
                  href={`mailto:${lender.ae.email}`}
                  onClick={stop}
                  className="hover:text-neutral-900 transition truncate max-w-[180px]"
                  title={lender.ae.email}
                >{lender.ae.email}</a>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-1 pt-3 border-t hairline-soft text-[10.5px] italic text-neutral-400">
          No account executive on file{isAdmin ? ' — click to add' : ''}
        </div>
      )}
    </div>
  );
}

export default function Lenders() {
  const { user, isAdmin } = useAuth();
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | lender-object

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/lenders`);
      setLenders(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lenders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lenders;
    return lenders.filter((l) => {
      const hay = [
        l.name, l.channel, l.ae?.name, l.ae?.email, l.ae?.phone,
        ...(l.specialties || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [lenders, query]);

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 tracking-tight flex items-center gap-2">
            <Landmark className="h-6 w-6 text-neutral-700" /> Lenders
          </h1>
          <p className="text-[12.5px] text-neutral-500 mt-1">
            {loading ? 'Loading…' : `${visible.length} of ${lenders.length} lenders`}
            {' · '}Contact your AE directly from any card
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            data-testid="lenders-new-btn"
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-neutral-900 text-[hsl(var(--kf-gold))] text-[12.5px] font-bold hover:bg-neutral-800 transition"
          >
            <Plus className="h-4 w-4" /> New Lender
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            data-testid="lenders-search"
            type="search"
            placeholder="Search lenders, AEs, specialties…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-xl border hairline bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-neutral-400 text-[13px]">Loading lenders…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 border hairline border-dashed rounded-xl bg-neutral-50/50">
          <Building2 className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
          <div className="text-[13px] text-neutral-500">
            {lenders.length === 0 ? 'No lenders yet.' : 'No lenders match your search.'}
          </div>
          {isAdmin && lenders.length === 0 && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-700 hover:text-neutral-900"
            >
              <Plus className="h-3.5 w-3.5" /> Add your first lender
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map((l) => (
            <LenderCard key={l.id} lender={l} isAdmin={isAdmin} onEdit={setEditing} />
          ))}
        </div>
      )}

      {editing && (
        <LenderModal
          mode={editing === 'new' ? 'create' : 'edit'}
          lender={editing === 'new' ? null : editing}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={(saved, deleted) => {
            setEditing(null);
            if (deleted) {
              setLenders((prev) => prev.filter((x) => x.id !== deleted));
              return;
            }
            setLenders((prev) => {
              const idx = prev.findIndex((x) => x.id === saved.id);
              if (idx === -1) return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name));
              const next = [...prev];
              next[idx] = saved;
              // Renamed lenders should re-sort to their new alphabetical spot
              // so the grid stays intuitive after an admin edits the name.
              return next.sort((a, b) => a.name.localeCompare(b.name));
            });
          }}
          onPatched={(patched) => {
            // Called by side-effect PATCHes (logo upload) that MUST NOT close
            // the modal. Merges the row in-place so the grid reflects the
            // change while the LO keeps typing.
            setLenders((prev) => prev.map((x) => (x.id === patched.id ? patched : x)));
          }}
        />
      )}
    </div>
  );
}
